<script setup>
import { ref, watch, computed } from 'vue';
import { Crown, Flame, X } from 'lucide-vue-next';
import {usePage} from "@inertiajs/vue3";
import {trackFathomEvent} from "@/Helpers/analytics";
import {trackAuditionAnalyticsEvent} from "@/Helpers/auditions";

const STAR_ICON_URL = 'https://sm.naughtycdn.com/images/landers/prize/NA-Star.png'

const props = defineProps({
    isOpen: Boolean,
    audition: {
        type: Object,
        default: null
    },
    source: {
        type: String,
        default: ''
    },
    openOnSwipe: {
        type: Boolean,
        default: false
    },
    allowClose: {
        type: Boolean,
        default: true
    }
});
const emit = defineEmits(['close']);

const isVisible = ref(false);
const email = ref('');

const isAuditionsContext = computed(() => !!props.audition);
const progressPercentage = computed(() => {
    const result = (props.audition?.likes / props.audition?.star_goal) * 100
    return Math.min(Math.round(result), 100);
});
const starsNeeded = computed(() => {
    if (!props.audition) return 0;
    return props.audition.star_goal - props.audition.likes;
});
const performerFirstName = computed(() => {
    if (!props.audition?.creator?.name) return null;
    return props.audition.creator.name.split(' ')[0];
});

const nearGoalMessage = computed(() => {
    if (!props.audition?.is_near_goal) return null;

    const pct = (props.audition.likes / props.audition.star_goal) * 100;
    const remaining = props.audition.star_goal - props.audition.likes;

    if (pct >= 90) return { text: `${remaining} star${remaining === 1 ? '' : 's'} away — will YOU be the closer?`, hot: true };
    if (pct >= 85) return { text: "Who's going to push it over? Could be you!", hot: true };
    return { text: "The community is making this happen!", hot: true };
});

const generalProgressMessage = computed(() => {
    if (!props.audition || props.audition?.is_near_goal || props.audition?.likes >= props.audition?.star_goal) return null;
    const pct = progressPercentage.value;
    if (pct < 25) return "Be an early backer — every star counts";
    if (pct < 50) return "Growing fast — join the community backing this";
    if (pct < 75) return "Over halfway there — help make this happen";
    return "Almost at the goal — your stars matter now";
});
const currentUrl = computed(() => {
    if (typeof window !== 'undefined' && window.location) {
        return window.location.pathname + window.location.search;
    }
    return '';
});

watch(() => props.isOpen, (open) => {
    isVisible.value = open;
    if (!open) email.value = '';
}, { immediate: true });

const joinOptionsData = usePage().props.joinOptions

const handleJoin = () => {
    // const source = 'Star_Power_Popup';
    const joinType = 'trial';

    if (joinOptionsData.button_trial) {
        let joinUrl = joinOptionsData.button_trial.url +
            (joinOptionsData.button_trial.url.includes('?') ? '&' : '?') +
            `custom8=${props.source}&vendor_option_id=2007234`;

        if (email.value.trim()) {
            joinUrl += `&email=${encodeURIComponent(email.value.trim())}`;
        }

        console.log(`++ JoinOptions: From ${props.source} Redirecting to`, joinUrl);

        localStorage.setItem('blue_join_options_source', props.source);
        localStorage.setItem('blue_join_options_join_type', joinType);
        localStorage.setItem('blue_join_options_url', joinUrl);
        if (props.audition) {
            trackAuditionAnalyticsEvent('Biller Join Button Clicked', props.audition, {
                store_in_local_storage: true,
                local_storage_key: 'blue_join_options_video_props',
                append_data: {
                    source: props.source,
                    join_type: joinType
                }
            });
            trackFathomEvent('Biller Join Button Clicked')
        }

        window.location.href = joinUrl;
    }
}
</script>

<template>
    <Teleport to="body">
        <div
            v-if="isOpen"
            class="fixed inset-0 z-[51] flex items-center justify-center p-4 sm:p-6"
        >
            <!-- Backdrop -->
            <div
                class="absolute inset-0 bg-black/80 backdrop-blur-sm transition-opacity duration-300"
                :class="isVisible ? 'opacity-100' : 'opacity-0'"
                aria-hidden="true"
            />

            <!-- Modal Container -->
            <div
                class="relative w-full max-w-[440px] max-h-[90vh] transform overflow-y-auto rounded-3xl bg-zinc-950 border border-zinc-800 shadow-2xl transition-all duration-300 ease-out"
                :class="isVisible ? 'scale-100 opacity-100 translate-y-0' : 'scale-95 opacity-0 translate-y-4'"
                role="dialog"
                aria-modal="true"
                aria-labelledby="modal-title"
            >
                <!-- Close Button -->
                <button
                    v-if="allowClose"
                    class="absolute right-4 top-4 p-2 text-zinc-500 hover:text-white transition-colors rounded-full hover:bg-zinc-900"
                    aria-label="Close modal"
                    @click="$emit('close')"
                >
                    <X class="h-5 w-5" />
                </button>

                <div class="p-4 sm:p-6 md:p-8">
                    <!-- Header Icon -->
                    <div class="flex justify-center mb-4 sm:mb-6">
                        <div class="relative">
                            <div class="absolute inset-0 bg-blue-500/20 blur-xl rounded-full" />
                            <div class="relative flex items-center justify-center w-20 h-20 bg-gradient-to-b from-zinc-800 to-zinc-950 rounded-full border border-zinc-700 shadow-inner">
                                <img
                                    :src="STAR_ICON_URL"
                                    alt="Naughty America"
                                    class="w-10 h-10"
                                />
                            </div>
                            <div class="absolute -top-1 left-1/2 -translate-x-1/2 bg-amber-500 text-zinc-950 text-[10px] font-bold px-2 py-0.5 rounded-full border border-zinc-900">
                                FREE
                            </div>
                        </div>
                    </div>

                    <!-- Text Content -->
                    <div class="text-center mb-5 sm:mb-8">
                        <h2
                            id="modal-title"
                            class="text-xl sm:text-2xl font-bold text-white mb-2 sm:mb-3 tracking-tight"
                        >
                            Activate FREE Account
                            <span class="text-zinc-500 font-normal text-base sm:text-lg block sm:inline sm:text-2xl">
                                (quick verify)
                            </span>
                        </h2>
                        <p class="text-zinc-400 text-xs sm:text-sm leading-relaxed">
                            Your sponsorship turns auditions into premium productions. Support
                            your favorite performers and help decide what content gets made.
                        </p>
                    </div>

                    <!-- Audition Context: Progress & Stats -->
                    <div
                        v-if="isAuditionsContext"
                        class="bg-zinc-900/50 rounded-xl p-4 sm:p-5 mb-4 sm:mb-6 border border-zinc-800/50"
                    >
                        <!-- Performer Info -->
                        <div class="flex items-center gap-3 mb-3 sm:mb-4">
                            <div
                                class="relative w-12 h-12 rounded-lg overflow-hidden ring-2 ring-zinc-700 shrink-0"
                            >
                                <img
                                    :src="audition.creator?.avatar"
                                    :alt="audition.creator?.name"
                                    class="w-full h-full object-cover"
                                />
                            </div>
                            <div class="min-w-0">
                                <p class="text-sm font-semibold text-white">
                                    {{ audition.creator?.name }}
                                </p>
                                <p class="text-xs text-zinc-400 line-clamp-1">
                                    {{ audition.title }}
                                </p>
                            </div>
                        </div>

                        <!-- Progress Bar -->
                        <div class="mb-3 sm:mb-4">
                            <div class="flex justify-between items-end mb-2">
                                <div class="text-xs sm:text-sm font-medium text-white flex items-center gap-1.5">
                                    <img :src="STAR_ICON_URL" alt="star" class="w-4 h-4" />
                                    <span>{{ audition.likes }}</span>
                                    <span class="text-zinc-500 font-normal">/ {{ audition.star_goal }} funded</span>
                                </div>
                                <div class="flex items-center gap-1.5 sm:gap-2">
                                    <span
                                        v-if="starsNeeded > 0"
                                        class="text-[10px] sm:text-xs font-semibold text-emerald-400"
                                    >
                                        {{ starsNeeded }} needed
                                    </span>
                                    <span class="text-[10px] sm:text-xs font-medium text-blue-400">
                                        {{ progressPercentage }}%
                                    </span>
                                </div>
                            </div>
                            <div class="h-2 w-full bg-zinc-800 rounded-full overflow-hidden">
                                <div
                                    class="h-full bg-gradient-to-r from-blue-600 to-blue-400 rounded-full transition-all duration-1000 ease-out"
                                    :style="{ width: `${progressPercentage}%` }"
                                />
                            </div>

                            <!-- Near goal FOMO line -->
                            <div
                                v-if="nearGoalMessage"
                                class="fomo-slide-in mt-2 flex items-center gap-1.5"
                            >
                                <span v-if="nearGoalMessage.hot" class="fomo-fire text-sm">🔥</span>
                                <span
                                    class="text-xs font-semibold"
                                    :class="nearGoalMessage.hot ? 'text-rose-400' : 'text-amber-400'"
                                >{{ nearGoalMessage.text }}</span>
                            </div>

                            <!-- General progress social proof (shown when not near goal) -->
                            <div
                                v-if="generalProgressMessage"
                                class="fomo-slide-in mt-2 flex items-center gap-1.5"
                            >
                                <span class="text-sm">⭐</span>
                                <span class="text-xs font-semibold text-amber-400">{{ generalProgressMessage }}</span>
                            </div>
                        </div>

                        <!-- Stats -->
                        <div class="space-y-2.5">
                            <div
                                v-if="audition.today_sponsors_count > 0"
                                class="flex items-center gap-3 text-sm text-zinc-300"
                            >
                                <div
                                    class="w-6 h-6 rounded-full bg-amber-500/10 flex items-center justify-center shrink-0"
                                >
                                    <Crown class="w-3.5 h-3.5 text-amber-500" />
                                </div>
                                <span>
                                    <strong class="text-white">{{ audition.today_sponsors_count }}</strong>
                                    backed today — <strong class="text-amber-400">be next</strong>
                                </span>
                            </div>
                            <div v-else class="flex items-center gap-3 text-sm text-zinc-300">
                                <div
                                    class="w-6 h-6 rounded-full bg-amber-500/10 flex items-center justify-center shrink-0"
                                >
                                    <Crown class="w-3.5 h-3.5 text-amber-500" />
                                </div>
                                <span class="text-white">
                                    No sponsors today — <strong>be the first!</strong>
                                </span>
                            </div>
                            <div
                                v-if="audition.top_sponsor_stars > 0"
                                class="flex items-center gap-3 text-sm text-zinc-300"
                            >
                                <div
                                    class="w-6 h-6 rounded-full bg-orange-500/10 flex items-center justify-center shrink-0"
                                >
                                    <Flame class="w-3.5 h-3.5 text-orange-500" />
                                </div>
                                <span>
                                    Top sponsor gave
                                    <strong class="text-white">{{ audition.top_sponsor_stars }} Stars</strong>
                                </span>
                            </div>
                        </div>
                    </div>

                    <!-- Optional Email -->
                    <div class="mb-4">
                        <input
                            v-model="email"
                            type="email"
                            placeholder="Email (optional)"
                            class="w-full px-4 py-3 bg-zinc-900 border border-zinc-700 hover:border-zinc-600 focus:border-blue-500 rounded-xl text-white text-sm placeholder:text-zinc-500 focus:outline-none transition-colors"
                        />
                    </div>

                    <!-- Actions -->
                    <div class="space-y-4">
                        <button
                            @click="handleJoin"
                            class="group relative overflow-hidden inline-flex items-center justify-center rounded-xl font-medium transition-all focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50 disabled:pointer-events-none active:scale-[0.98] bg-blue-600 text-white hover:bg-blue-500 shadow-lg shadow-blue-500/25 border border-blue-500 h-14 px-4 sm:px-6 text-base sm:text-lg w-full flex-col h-auto py-3 sm:py-4 gap-1"
                        >
                            <div class="absolute inset-0 bg-gradient-to-r from-blue-600 to-blue-500 opacity-100 transition-opacity group-hover:opacity-90" />
                            <div class="relative z-10 flex items-center justify-center gap-1 sm:gap-2 text-base sm:text-lg font-bold leading-tight">
                                <span class="text-center">Give {{ performerFirstName ? performerFirstName + ' ' : '' }}5 FREE Stars - $0.00*</span>
                            </div>
                        </button>

                        <div class="text-center">
                            <div class="relative z-10 text-[11px] font-medium text-zinc-500 opacity-90">
                                *Get 3 days free, cancel anytime.
                            </div>
                            <p v-if="!openOnSwipe" class="text-center text-sm text-zinc-500 mt-2">
                                Already a member?
                                <a
                                    :href="route('members2.auth.login', { dest: currentUrl })"
                                    class="text-blue-400 hover:text-blue-300 font-semibold transition-colors"
                                >
                                    Sign in
                                </a>
                            </p>
                            <button
                                v-if="openOnSwipe"
                                class="text-sm text-zinc-500 hover:text-zinc-300 transition-colors font-medium px-4 py-2 mt-2 rounded-lg hover:bg-zinc-900"
                                @click="$emit('close', 'dont-show-again')"
                            >
                                Remind me later
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </Teleport>
</template>

<style scoped>
@keyframes fomo-slide-in {
    from { opacity: 0; transform: translateY(-4px); }
    to   { opacity: 1; transform: translateY(0); }
}

.fomo-slide-in {
    animation: fomo-slide-in 0.4s ease-out both;
}

@keyframes fomo-fire-flicker {
    0%, 100% { transform: rotate(-8deg) scale(1);    }
    25%       { transform: rotate(6deg)  scale(1.15); }
    50%       { transform: rotate(-4deg) scale(0.95); }
    75%       { transform: rotate(8deg)  scale(1.1);  }
}

.fomo-fire {
    display: inline-block;
    animation: fomo-fire-flicker 1.4s ease-in-out infinite;
    transform-origin: bottom center;
}
</style>
